import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { Truck, Package, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "Freight / LTL"];

function ShipForm({ order, onShipped }) {
  const [carrier, setCarrier] = useState("UPS");
  const [tracking, setTracking] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!tracking.trim()) { toast.error("Enter a tracking number"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.order_number}/ship`, {
        carrier, tracking_number: tracking.trim(), tracking_url: url.trim() || null,
      });
      toast.success(`Marked ${order.order_number} shipped — email sent to ${order.email}`);
      onShipped();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark shipped");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 grid sm:grid-cols-4 gap-3 items-end" data-testid={`ship-form-${order.order_number}`}>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Carrier</label>
        <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`ship-carrier-${order.order_number}`} className="w-full bg-[#111] border border-[#27272a] py-2 px-2 text-sm outline-none focus:border-[#00f0ff]">
          {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Tracking #</label>
        <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`ship-tracking-${order.order_number}`} className="w-full bg-[#111] border border-[#27272a] py-2 px-2 text-sm outline-none focus:border-[#00f0ff] font-mono" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Tracking URL (optional)</label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`ship-url-${order.order_number}`} placeholder="https://..." className="w-full bg-[#111] border border-[#27272a] py-2 px-2 text-sm outline-none focus:border-[#00f0ff]" />
      </div>
      <button type="submit" disabled={busy} data-testid={`ship-submit-${order.order_number}`} className="bttf-gradient text-black font-bold uppercase tracking-wider text-sm py-2.5 disabled:opacity-50 hover:opacity-90 transition-opacity">
        {busy ? "Sending..." : "Mark Shipped"}
      </button>
    </form>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user === null) return <div className="pt-40 pb-40 text-center text-zinc-500">Loading...</div>;

  if (!user || user.role !== "admin") {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="admin-denied">
        <h1 className="font-display text-5xl uppercase">Admin Only</h1>
        <p className="text-zinc-400 mt-4">You need an administrator account to view this page.</p>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-[#00f0ff]">Fulfillment</span>
          <h1 className="font-display text-6xl md:text-7xl uppercase leading-none mt-3">Orders</h1>
        </Reveal>

        <div className="mt-10 space-y-5" data-testid="admin-orders">
          {orders === null ? (
            <div className="text-zinc-500">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-zinc-400 py-16 text-center border border-[#27272a]" data-testid="admin-no-orders">No orders yet.</div>
          ) : (
            orders.map((o) => (
              <div key={o.order_number} className="border border-[#27272a] bg-[#0a0a0a] p-5" data-testid={`admin-order-${o.order_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-[#00f0ff] text-sm">{o.order_number}</div>
                    <div className="text-sm text-zinc-300 mt-1">{o.full_name} · {o.email}</div>
                    <div className="text-xs text-zinc-500">{o.address}, {o.city}, {o.state || ""} {o.zip_code}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{money(o.total)}</div>
                    <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider mt-1 ${o.status === "shipped" ? "text-emerald-400" : "text-amber-400"}`}>
                      {o.status === "shipped" ? <CheckCircle2 size={13} /> : <Package size={13} />} {o.status}
                    </span>
                  </div>
                </div>

                <div className="mt-3 text-xs text-zinc-400 flex flex-wrap gap-x-4 gap-y-1">
                  {o.items?.map((it, i) => <span key={i}>{it.quantity}× {it.name}</span>)}
                </div>

                {o.status === "shipped" ? (
                  <div className="mt-4 flex items-center gap-2 text-sm text-emerald-400" data-testid={`shipped-info-${o.order_number}`}>
                    <Truck size={15} /> Shipped via {o.carrier} — <span className="font-mono">{o.tracking_number}</span>
                  </div>
                ) : (
                  <ShipForm order={o} onShipped={load} />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
